<template>
  <Field
    as="div"
    :name="name || ''"
    :rules="rules"
    :model-value="currentValue"
    v-slot="{ field, errorMessage }"
  >
    <div class="relative w-full">
      <InputLabel
        :class="errorMessage ? 'text-error' : 'text-text'"
        :for="id"
        :placeholder="placeholder"
        :value="currentValue"
      />
      <textarea
        v-bind="field"
        :id="id"
        :value="currentValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :readonly="readonly"
        ref="inputRef"
        :class="[
          'px-4 placeholder-transparent w-full border rounded-sm bg-surface-light h-14 text-headings  focus:outline-none focus:ring-1 focus:ring-input-border transition-colors',
          currentValue ? 'pt-6 pb-2' : 'py-4',
          errorMessage ? 'border-error text-error' : 'border-input-border ',
        ]"
        @input="onInput"
        @blur="onBlur"
        @focus="onFocus"
      />
    </div>
    <div v-if="errorMessage" class="text-sm block pt-2 text-error">
      {{ errorMessage }}
    </div>
  </Field>
</template>

<script setup lang="ts">
  import { Field } from 'vee-validate';
  import { computed,ref } from 'vue';

  import InputLabel from './InputLabel.vue';
  import type { InputProps } from './InputMixin';

  interface Props extends InputProps<string> {
    placeholder?: string;
    required?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {});

  const emit = defineEmits<{
    'update:modelValue': [value: string];
    focus: [event: FocusEvent];
    blur: [event: FocusEvent];
  }>();

  const internalValue = ref(props.defaultValue);

  const currentValue = computed(() => {
    return props.modelValue !== undefined
      ? props.modelValue
      : internalValue.value;
  });

  function updateValue(newValue: string) {
    if (props.modelValue !== undefined) {
      emit('update:modelValue', newValue);
    } else {
      internalValue.value = newValue;
    }
  }

  function onInput(event: Event) {
    const target = event.target as HTMLInputElement;

    updateValue(target.value);
  }

  function onBlur(event: FocusEvent) {
    emit('blur', event);
  }

  function onFocus(event: FocusEvent) {
    emit('focus', event);
  }
</script>
